import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Container, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { request } from './OperationsProvider';
import { sessionActions } from '../store';
import { formatDateTime } from '../common/util/datetime';
import Wordmark from '../shell/Wordmark';
import PlatformDevices from './PlatformDevices';
import PlatformTpms from './PlatformTpms';

const blank = {
  mode: 'create', id: null, name: '', appName: '', slug: '', adminName: '', email: '', password: '', logoDataUrl: '', brand: '',
};
const tenantUrl = (slug) => `${window.location.origin}/t/${slug}`;

const PlatformPage = () => {
  const [tenants, setTenants] = useState([]); const [search, setSearch] = useState(''); const [form, setForm] = useState(null);
  const [created, setCreated] = useState(null); const [copied, setCopied] = useState(false);
  const [removing, setRemoving] = useState(null); const [confirmText, setConfirmText] = useState('');
  const [pending, setPending] = useState(false); const [error, setError] = useState(null); const [notice, setNotice] = useState(null);
  const dispatch = useDispatch(); const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const productName = (key) => products.find((p) => p.key === key)?.name || key;
  const refresh = async () => setTenants(await request('/api/platform/tenants'));
  useEffect(() => {
    refresh().catch((e) => setError(e.message));
    // The products this platform sells under. Asked for rather than compiled in, because this file
    // is served to every customer of every one of them.
    request('/api/platform/brands').then((list) => {
      setProducts(list);
      // The form opens on the first product rather than on one named here.
      setForm((f) => (f && !f.brand ? { ...f, brand: list[0]?.key || '' } : f));
    }).catch(() => setProducts([]));
  }, []);

  const editing = form?.mode === 'edit';

  // Mirrors the tenant-side branding panel: reject here rather than let the server
  // reject after the operator has filled in the whole form.
  const chooseLogo = (event) => {
    const file = event.target.files[0]; if (!file) return;
    setError(null);
    if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > 256 * 1024) { setError('Use a PNG/JPEG up to 256 KB.'); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logoDataUrl: reader.result }));
    reader.onerror = () => setError('Could not read this file.');
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault(); setPending(true); setError(null);
    try {
      if (editing) {
        await request(`/api/platform/tenants/${form.id}`, 'PATCH', {
          name: form.name, appName: form.appName, slug: form.slug, logoDataUrl: form.logoDataUrl,
        });
        setNotice(`${form.appName || form.name} updated.`);
        setForm(null);
      } else {
        const tenant = await request('/api/platform/tenants', 'POST', form);
        // The form is cleared on success, so carry the administrator's email into the
        // result dialog rather than reading it back off state that no longer exists.
        setCreated({ ...tenant, adminEmail: form.email });
        setForm(null); setCopied(false);
      }
      await refresh();
    } catch (err) { setError(err.message); } finally { setPending(false); }
  };

  const destroy = async () => {
    setPending(true); setError(null);
    try {
      await request(`/api/platform/tenants/${removing.id}`, 'DELETE');
      setNotice(`${removing.name} and all of its records were deleted.`);
      setRemoving(null); setConfirmText(''); await refresh();
    } catch (err) { setError(err.message); } finally { setPending(false); }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); setCopied(true); } catch { setCopied(false); }
  };

  const logout = async () => { await request('/api/session', 'DELETE'); dispatch(sessionActions.updateUser(null)); navigate('/login'); };

  return (
    <Container maxWidth="lg" sx={{ py: 5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="overline">
            <Wordmark />
            {' PLATFORM'}
          </Typography>
          <Typography variant="h4">Tenants</Typography>
        </Box>
        <Stack direction="row" gap={2}>
          <Button variant="contained" onClick={() => { setError(null); setForm({ ...blank, brand: products[0]?.key || '' }); }}>Add tenant</Button>
          <Button onClick={logout}>Sign out</Button>
        </Stack>
      </Stack>
      <Typography color="text.secondary" sx={{ my: 2 }}>Onboard customer organizations and their first administrator. Operational records remain accessible only within each tenant.</Typography>
      {error && !form && !removing && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)} sx={{ my: 1 }}>{notice}</Alert>}
      <TextField label="Search tenants" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ my: 2 }} />
      <Paper variant="outlined">
        <Table>
          <TableHead><TableRow>{['Organization', 'App name', 'Product', 'Sign-in URL', 'Users', 'Created', ''].map((c, i) => <TableCell key={c || `actions${i}`} align={c === '' ? 'right' : 'left'}>{c}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {tenants.filter((t) => t.name.toLowerCase().includes(search.toLowerCase())).map((t) => (
              <TableRow key={t.id}>
                <TableCell>{t.name}</TableCell>
                <TableCell>{t.appName || t.name}</TableCell>
                <TableCell>{productName(t.brand || products[0]?.key)}</TableCell>
                <TableCell>
                  {t.slug ? (
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <Link href={tenantUrl(t.slug)} target="_blank" rel="noopener" sx={{ fontFamily: 'monospace', fontSize: 13 }}>{`/t/${t.slug}`}</Link>
                      <Tooltip title="Copy sign-in URL">
                        <IconButton size="small" onClick={() => copy(tenantUrl(t.slug))}><ContentCopyIcon fontSize="inherit" /></IconButton>
                      </Tooltip>
                    </Stack>
                  ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                </TableCell>
                <TableCell>{t.userCount}</TableCell>
                <TableCell>{formatDateTime(t.createdAt)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit tenant">
                    <IconButton size="small" onClick={() => { setError(null); setForm({ ...blank, mode: 'edit', id: t.id, name: t.name, appName: t.appName || t.name, slug: t.slug || '', logoDataUrl: t.logoDataUrl || '' }); }}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete tenant">
                    <IconButton size="small" color="error" onClick={() => { setError(null); setConfirmText(''); setRemoving(t); }}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* The GPS hardware this platform operates. It sits under the tenants because granting a
          device needs them: the registry is the platform's, the vehicles are theirs. */}
      <PlatformDevices tenants={tenants} />

      {/* Tyre monitoring and its simulator, which is a platform responsibility for the same reason:
          turning a customer's lorry from real readings to invented ones is not their decision. */}
      <PlatformTpms tenants={tenants} />

      <Dialog open={!!form} onClose={() => !pending && setForm(null)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={submit}>
          <DialogTitle>{editing ? 'Edit tenant' : 'Add tenant'}</DialogTitle>
          <DialogContent>
            <Stack gap={2} sx={{ pt: 1 }}>
              {error && <Alert severity="error">{error}</Alert>}
              {form && (
                <>
                  <TextField required label="Organization name" inputProps={{ maxLength: 500 }} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                  <TextField label="App name" helperText="Shown on their sign-in page and on reports. Defaults to the organization name." inputProps={{ maxLength: 500 }} value={form.appName} onChange={(e) => setForm((f) => ({ ...f, appName: e.target.value }))} />
                  {!editing && (
                    <TextField
                      select
                      required
                      label="Product"
                      helperText="Which product this workspace is sold under. It decides the address their people sign in at, and cannot be changed afterwards."
                      value={form.brand}
                      onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}
                    >
                      {products.map((p) => <MenuItem key={p.key} value={p.key}>{p.name}</MenuItem>)}
                    </TextField>
                  )}
                  {editing && (
                    <TextField
                      label="Sign-in URL"
                      helperText={`Currently ${tenantUrl(form.slug || '')}. Changing this breaks the address already given to the tenant.`}
                      inputProps={{ maxLength: 60 }}
                      value={form.slug}
                      onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                    />
                  )}
                  {form.logoDataUrl && <Box component="img" src={form.logoDataUrl} alt="Logo preview" sx={{ maxWidth: 240, height: 100, objectFit: 'contain', alignSelf: 'start' }} />}
                  <Stack direction="row" gap={2} alignItems="center">
                    <Button component="label" variant="outlined">
                      {form.logoDataUrl ? 'Replace logo' : 'Choose logo'}
                      <input hidden type="file" accept="image/png,image/jpeg" onChange={chooseLogo} />
                    </Button>
                    {form.logoDataUrl && <Button onClick={() => setForm((f) => ({ ...f, logoDataUrl: '' }))}>Remove</Button>}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">PNG or JPEG · Up to 256 KB · Maximum 2048 × 2048 pixels. The tenant administrator can change this later.</Typography>
                  {!editing && (
                    <>
                      <TextField required label="Administrator name" inputProps={{ maxLength: 500 }} value={form.adminName} onChange={(e) => setForm((f) => ({ ...f, adminName: e.target.value }))} />
                      <TextField required type="email" label="Administrator email" inputProps={{ maxLength: 500 }} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                      <TextField required type="password" label="Initial password" inputProps={{ minLength: 12, maxLength: 256 }} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
                    </>
                  )}
                  {editing && <Alert severity="info">Editing changes this workspace&apos;s identity only. Its users, vehicles and trips are untouched, and administrator accounts are managed inside the tenant.</Alert>}
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={pending} onClick={() => setForm(null)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={pending}>{editing ? 'Save changes' : 'Create tenant'}</Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={!!removing} onClose={() => !pending && setRemoving(null)} fullWidth maxWidth="sm">
        <DialogTitle>{`Delete ${removing?.name}?`}</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Alert severity="error">
              This permanently deletes the workspace and everything recorded in it —
              {' '}
              {`${removing?.userCount ?? 0} user account(s)`}
              , vehicles, assignments, checkpoints, trips and reports. It cannot be undone.
            </Alert>
            <Typography color="text.secondary">
              Type
              {' '}
              <strong>{removing?.name}</strong>
              {' '}
              to confirm.
            </Typography>
            <TextField autoFocus label="Organization name" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={pending} onClick={() => { setRemoving(null); setConfirmText(''); }}>Cancel</Button>
          <Button color="error" variant="contained" disabled={pending || confirmText !== removing?.name} onClick={destroy}>Delete tenant</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!created} onClose={() => setCreated(null)} fullWidth maxWidth="sm">
        <DialogTitle>{`${created?.appName || created?.name} is ready`}</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ pt: 1 }}>
            <Typography color="text.secondary">Send this address to the tenant. It opens their sign-in page with their own name and logo.</Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Typography sx={{ fontFamily: 'monospace', fontSize: 14, wordBreak: 'break-all' }}>{created ? tenantUrl(created.slug) : ''}</Typography>
                <Tooltip title="Copy">
                  <IconButton onClick={() => copy(tenantUrl(created.slug))}><ContentCopyIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Stack>
            </Paper>
            {copied && <Alert severity="success">Copied to clipboard.</Alert>}
            <Alert severity="info">
              The administrator signs in with
              {' '}
              <strong>{created?.adminEmail}</strong>
              {' '}
              and the initial password you set. Credentials work from any sign-in page — this URL controls the branding, not access.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCreated(null)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
export default PlatformPage;
